import { computeFixedRevenue } from "./computeFixedRevenue";
import { isNonClientProject } from "./nonClientProjects";

export function calculateConsultantClientTable({
  hoursData,
  sellingRates,
  buyingCosts,
  overhead,
  periodFrom,
  periodTo,
}) {
  const from = new Date(periodFrom);
  const to = new Date(periodTo);

  const num = (x) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : 0;
  };

  const norm = (s) => String(s ?? "").trim();
  const normKey = (s) => norm(s).toLowerCase();

  const normProject = (s) =>
    norm(s)
      .toUpperCase()
      .replace(/\(.*?\)/g, "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const overlaps = (r) => {
    const f = new Date(r.date_from);
    const t = new Date(r.date_to);
    return f <= to && t >= from;
  };

  const pickBest = (items) => {
    const o = (items || []).filter(overlaps);
    if (!o.length) return null;
    o.sort((a, b) => new Date(b.date_from) - new Date(a.date_from));
    return o[0];
  };

  // Round a monetary amount to whole cents (finding #52). Applied only at the
  // final per-row aggregation, never to intermediate accumulators.
  const round2 = (x) => Math.round((num(x) + Number.EPSILON) * 100) / 100;

  // Rate row whose [date_from, date_to] contains a single day (ms); latest
  // date_from wins on ties. Mirrors pickBest but for an exact day.
  const pickForDay = (items, dayMs) => {
    let best = null;
    let bestFrom = -Infinity;
    (items || []).forEach((r) => {
      const f = new Date(r.date_from).getTime();
      const t = new Date(r.date_to).getTime();
      if (f <= dayMs && t >= dayMs && f >= bestFrom) {
        best = r;
        bestFrom = f;
      }
    });
    return best;
  };

  // Price one hours row per day so a mid-period rate change is honored instead
  // of applying one "best" row to the whole window (finding #25). 3-tier
  // fallback (exact Key -> empty Key -> User/Project) is applied per day. Falls
  // back to single-best-row period pricing when no hours_by_day breakdown exists.
  // Returns { amount, ratedHours, unratedHours }.
  // `tiers` is an ORDERED array of candidate-row arrays, most specific
  // first — a list rather than fixed arguments because the buy side gained
  // a fourth, person-level tier. Kept identical to calculateMargins.js: if
  // the two ever disagree, the dashboard and this table report different
  // costs for the same hours.
  const priceRowPerDay = (row, hours, tiers, rateField) => {
    const byDay = row.hours_by_day;
    if (!byDay || Object.keys(byDay).length === 0) {
      let r = null;
      for (const t of tiers) {
        r = pickBest(t);
        if (r) break;
      }
      if (!r) return { amount: 0, ratedHours: 0, unratedHours: hours };
      return { amount: hours * num(r[rateField]), ratedHours: hours, unratedHours: 0 };
    }

    let amount = 0;
    let ratedHours = 0;
    let unratedHours = 0;
    Object.entries(byDay).forEach(([dateStr, h]) => {
      const dayHours = num(h);
      if (dayHours <= 0) return;
      const dayMs = new Date(dateStr).getTime();
      if (dayMs < from.getTime() || dayMs > to.getTime()) return;
      let r = null;
      for (const t of tiers) {
        r = pickForDay(t, dayMs);
        if (r) break;
      }
      if (r) {
        amount += dayHours * num(r[rateField]);
        ratedHours += dayHours;
      } else {
        unratedHours += dayHours;
      }
    });
    return { amount, ratedHours, unratedHours };
  };

  // -------------------------
  // 1) Fixed per (User, Project): annual rate prorated by day, latest date_from wins
  // -------------------------
  const { fixedRevenueByUserProject, fixedUserProject } = computeFixedRevenue(
    sellingRates,
    periodFrom,
    periodTo
  );

  // -------------------------
  // 2) Index non-fixed sell/buy by User||Project||Key
  // -------------------------
  const sellByKey = {};
  const sellByUP = {};
  (sellingRates || []).forEach((r) => {
    if (r.fixed) return;
    const k = `${norm(r.User)}||${normProject(r.Project)}||${normKey(r.Key)}`;
    sellByKey[k] = sellByKey[k] || [];
    sellByKey[k].push(r);
    const up = `${norm(r.User)}||${normProject(r.Project)}`;
    sellByUP[up] = sellByUP[up] || [];
    sellByUP[up].push(r);
  });

  const buyByKey = {};
  const buyByUP = {};
  // Project-less cost rows apply to the person whoever they bill — that is
  // how Talexa's internal cost feed arrives. Mirrors calculateMargins.js.
  const buyByUser = {};
  (buyingCosts || []).forEach((r) => {
    const user = norm(r.User);
    const project = normProject(r.Project);
    if (!project) {
      buyByUser[user] = buyByUser[user] || [];
      buyByUser[user].push(r);
      return;
    }
    const k = `${user}||${project}||${normKey(r.Key)}`;
    buyByKey[k] = buyByKey[k] || [];
    buyByKey[k].push(r);
    const up = `${user}||${project}`;
    buyByUP[up] = buyByUP[up] || [];
    buyByUP[up].push(r);
  });

  // 3-tier candidate arrays (exact Key, empty Key, User/Project) for per-day pricing.
  const sellTiers = (user, project, key) => {
    const kExact = `${norm(user)}||${normProject(project)}||${normKey(key)}`;
    const kFallback = `${norm(user)}||${normProject(project)}||`;
    const up = `${norm(user)}||${normProject(project)}`;
    return [sellByKey[kExact], sellByKey[kFallback], sellByUP[up]];
  };

  // Four tiers: the three project-scoped ones, then the person-level
  // fallback for project-less cost rows. Only fires when the others miss.
  const buyTiers = (user, project, key) => {
    const kExact = `${norm(user)}||${normProject(project)}||${normKey(key)}`;
    const kFallback = `${norm(user)}||${normProject(project)}||`;
    const up = `${norm(user)}||${normProject(project)}`;
    return [
      buyByKey[kExact],
      buyByKey[kFallback],
      buyByUP[up],
      buyByUser[norm(user)],
    ];
  };

  // -------------------------
  // 3) OVERHEAD from period (already prorated)
  //    Equal split per internal consultant, then prorated across their projects by hours
  // -------------------------
  const overheadForPeriod = overhead?.annual_total ? num(overhead.annual_total) : 0;

  const internalUserHours = {};
  const internalUserProjectHours = {};

  (hoursData || []).forEach((r) => {
    if (r.__type !== "internal") return;

    const u = norm(r.User);
    const p = normProject(r.Project);
    const h = num(r.total_horas);
    if (!u || !p || h <= 0) return;

    // Hours on internal/non-billable categories (INTERNAL, VACATION, ...) must
    // NOT enter the overhead distribution: those projects carry no overhead and
    // are hidden from the views, so counting their hours in the denominator
    // would dilute the overhead landing on real clients and that diluted share
    // would silently vanish. Overhead is spread over billable client hours only.
    if (isNonClientProject(p)) return;

    internalUserHours[u] = (internalUserHours[u] || 0) + h;
    const k = `${u}||${p}`;
    internalUserProjectHours[k] = (internalUserProjectHours[k] || 0) + h;
  });

  const overheadByUserProject = {};
  const numInternalConsultants = Object.keys(internalUserHours).length;
  // Divide by the consultants who actually receive a share, so the whole
  // overhead lands somewhere. Kept identical to calculateMargins.js — if the
  // two denominators ever differ, this table and the dashboard report
  // different overhead for the same period. See the long note there for why
  // the overhead-row headcount was dropped (2026-08-06: €57,865 of 2025
  // overhead was being allocated to nobody).
  const overheadDenom = numInternalConsultants;

  if (overheadForPeriod > 0 && numInternalConsultants > 0 && overheadDenom > 0) {
    const overheadPerConsultant = overheadForPeriod / overheadDenom;

    Object.entries(internalUserHours).forEach(([u, totalUserHours]) => {
      Object.entries(internalUserProjectHours)
        .filter(([k]) => k.startsWith(`${u}||`))
        .forEach(([k, h]) => {
          overheadByUserProject[k] = overheadPerConsultant * (h / totalUserHours);
        });
    });
  }

  // -------------------------
  // 5) Aggregate hours/revenue/cost per (User, Project)
  // -------------------------
  const byUP = {};

  (hoursData || []).forEach((row) => {
    const user = norm(row.User);
    const project = normProject(row.Project);
    const key = normKey(row.Key);
    const hours = num(row.total_horas);
    if (!user || !project || hours <= 0) return;

    const up = `${user}||${project}`;
    const isInternal = row.__type === "internal";
    // Suppress hourly billing for any (User, Project) with a fixed rate in the
    // period — independent of Key (Chrona-imported hours carry no Key).
    const isFixed = fixedUserProject.has(`${user}||${project}`);

    // init row
    if (!byUP[up]) {
      byUP[up] = {
        user,
        project,
        type: isInternal ? "internal" : "external",
        hours: 0,
        hoursMissingRates: 0,
        hoursMissingCosts: 0,
        revenue: 0,
        directCost: 0,
        overhead: 0,
        margin: 0,
      };
    }

    // Fixed users: accumulate hours, direct cost and overhead normally
    // Only hourly revenue is skipped (fixed revenue is added separately in step 6)
    byUP[up].hours += hours;

    // Direct cost priced per day (still charged on fixed-rate projects, #25).
    // Hours no cost rate covers are counted too — priced at €0 they inflate
    // this row's margin, and until now nothing said so. Keep in step with
    // calculateMargins so the two views agree on what is missing.
    const buyPriced = priceRowPerDay(
      row, hours, buyTiers(user, project, key), "purchase_rate"
    );
    byUP[up].directCost += buyPriced.amount;
    byUP[up].hoursMissingCosts += buyPriced.unratedHours;

    // Hourly revenue only for non-fixed users; priced per day so mid-period rate
    // changes are honored (#25). Surface hours with no covering selling rate as
    // hoursMissingRates so €0-revenue hours don't blend invisibly (#23).
    // Fixed-UP hours are covered by the fixed revenue path, not "missing".
    if (!isFixed) {
      const sellPriced = priceRowPerDay(
        row, hours, sellTiers(user, project, key), "sales_rate"
      );
      byUP[up].revenue += sellPriced.amount;
      byUP[up].hoursMissingRates += sellPriced.unratedHours;
    }
  });

  // -------------------------
  // 6) Add fixed revenue (already prorated)
  // -------------------------
  Object.entries(fixedRevenueByUserProject).forEach(([up, fixedAmount]) => {
    const [user, project] = up.split("||");

    if (!byUP[up]) {
      // create row even if no hours counted
      byUP[up] = {
        user,
        project,
        type: "external",
        hours: 0,
        hoursMissingRates: 0,
        hoursMissingCosts: 0,
        revenue: 0,
        directCost: 0,
        overhead: 0,
        margin: 0,
      };
    }

    byUP[up].revenue += num(fixedAmount);
  });

  // If a fixed user is actually internal in hoursData, mark it internal + overhead
  const internalUsers = new Set((hoursData || []).filter(r => r.__type === "internal").map(r => norm(r.User)));
  Object.values(byUP).forEach((r) => {
    if (internalUsers.has(norm(r.user))) {
      r.type = "internal";
      const up = `${r.user}||${r.project}`;
      r.overhead = num(overheadByUserProject[up]);
    }
  });

  // -------------------------
  // 7) Margin calculation per row
  // -------------------------
  // Round monetary outputs to whole cents at final aggregation only (finding
  // #52) so this table reconciles to the cent with the by-client table and KPIs.
  Object.values(byUP).forEach((r) => {
    r.revenue = round2(r.revenue);
    r.directCost = round2(r.directCost);
    if (r.type === "internal") {
      r.overhead = round2(r.overhead);
      r.margin = round2(r.revenue - r.directCost - r.overhead);
    } else {
      r.overhead = 0;
      r.margin = round2(r.revenue - r.directCost);
    }
  });

  // return rows sorted
  // Exclude internal / non-billable categories (INTERNAL, VACATION, ...) from
  // the consultant×client view — they are not clients.
  return Object.values(byUP)
    .filter((r) => !isNonClientProject(r.project))
    .sort((a, b) => b.revenue - a.revenue);
}